import { find, uniqBy } from 'lodash'
import { ISelectedOrderRow } from '@/screens/Order/jotai/order.jotai'

export type ValidationError =
  | 'SUPPLIER_FACTORY_MISMATCH'
  | 'QTY_BREAKDOWN_MISMATCH'
  | 'MULTIPLE_MISMATCH'
  | 'EXTERNAL_ORDER'
  | null

export interface SelectionValidationResult {
  isValid: boolean
  validationError: ValidationError
  tooltipText: string | null
  warningText?: string | null
}


const getBreakdownValue = (order: ISelectedOrderRow): string | null =>
  find(
    order.property_values,
    (pv) => pv.field === 'quantityBreakdown' 
  )?.value ?? null

export function useOrderSelectionValidation(
  selectedRows: ISelectedOrderRow[],
  userCompanyId?: string | number
): SelectionValidationResult {
  if (!selectedRows.length) {
    return { isValid: false, validationError: null, tooltipText: null, warningText: null }
  }

  // Rule 1: all selected orders must share the same supplier AND factory
  const uniqueSuppliers = uniqBy(selectedRows, (row: any) => row.supplier?.id ?? row.supplier_id)
  const uniqueFactories = uniqBy(selectedRows, (row: any) => row.factory?.id ?? row.factory_id)
  const hasSupplierFactoryMismatch = uniqueSuppliers.length > 1 || uniqueFactories.length > 1

  // Rule 2: all selected orders must share the same quantity breakdown structure
  const uniqueBreakdowns = uniqBy(selectedRows, getBreakdownValue)
  const hasBreakdownMismatch = uniqueBreakdowns.length > 1

  // Rule 3: Cannot select external orders
  const hasExternalOrder = selectedRows.some((row: any) => {
    const orderCompanyId = row.company?.id ?? row.company_id
    return orderCompanyId && userCompanyId && String(orderCompanyId) !== String(userCompanyId)
  })

  const mismatchItems: string[] = []
  if (hasSupplierFactoryMismatch) mismatchItems.push('Supplier/Factory')
  if (hasBreakdownMismatch) mismatchItems.push('Quantity Breakdown')

  const mismatchText = mismatchItems.length > 0 
    ? `${mismatchItems.join(' and ')} must be the same.` 
    : ''

  let tooltipText = ''
  if (hasExternalOrder && mismatchText) {
    tooltipText = `External orders are not allowed, and ${mismatchText}`
  } else if (hasExternalOrder) {
    tooltipText = 'External orders are not allowed.'
  } else if (mismatchText) {
    tooltipText = mismatchText
  }

  const deliveredCount = selectedRows.filter((row: any) => row.is_delivered === true).length
  let warningText = null
  if (deliveredCount > 0) {
    warningText = `This selection includes ${deliveredCount} delivered order${deliveredCount > 1 ? 's' : ''}. Review before continuing.`
  }

  if (tooltipText) {
    return {
      isValid: false,
      validationError: 'MULTIPLE_MISMATCH',
      tooltipText,
      warningText,
    }
  }

  return { isValid: true, validationError: null, tooltipText: null, warningText }
}
